import type { SidebarGroup } from "@plerity/dashboard-shell";
import { createShell, PORTAL_URLS, type SettingsTab } from "@plerity/dashboard-shell";
import {
  CalendarDays,
  Clock,
  CreditCard,
  DoorOpen,
  Globe,
  LayoutDashboard,
  ListChecks,
  Package2,
  Settings,
  Users,
  Wrench,
} from "lucide-react";
import { BookingSettingsTabContent } from "./routes/settings/SettingsTabContent";

const sidebarGroups: SidebarGroup[] = [
  {
    title: "Booking",
    routes: [
      {
        path: "dashboard",
        label: "Overview",
        icon: LayoutDashboard,
        loader: () => import("./routes/booking/BookingDashboard"),
      },
      {
        path: "manage/:cancelToken",
        label: "Manage Booking",
        icon: CalendarDays,
        loader: () => import("./routes/booking/ManageBookingPage"),
        hidden: true,
      },
      {
        path: "appointments",
        label: "Appointments",
        icon: CalendarDays,
        loader: () => import("./routes/booking/AppointmentsPage"),
      },
      {
        path: "services",
        label: "Services",
        icon: Wrench,
        loader: () => import("./routes/booking/ServicesPage"),
      },
    ],
  },
  {
    title: "Management",
    routes: [
      {
        path: "staff",
        label: "Staff",
        icon: Users,
        loader: () => import("./routes/booking/StaffPage"),
      },
      {
        path: "rooms",
        label: "Rooms",
        icon: DoorOpen,
        loader: () => import("./routes/booking/RoomsPage"),
      },
      {
        path: "clients",
        label: "Clients",
        icon: ListChecks,
        loader: () => import("./routes/booking/ClientsPage"),
      },
      {
        path: "packages",
        label: "Packages",
        icon: Package2,
        loader: () => import("./routes/booking/PackagesPage"),
      },
      {
        path: "memberships",
        label: "Memberships",
        icon: CreditCard,
        loader: () => import("./routes/booking/MembershipsPage"),
      },
    ],
  },
  {
    title: "Configuration",
    routes: [
      {
        path: "waitlist",
        label: "Waitlist",
        icon: Clock,
        loader: () => import("./routes/booking/WaitlistPage"),
      },
      {
        path: "public-booking",
        label: "Public Booking",
        icon: Globe,
        loader: () => import("./routes/booking/PublicBookingPage"),
      },
      {
        path: "payments",
        label: "Payments",
        icon: CreditCard,
        loader: () => import("./routes/booking/PaymentsPage"),
      },
    ],
  },
  {
    // Hidden legacy settings redirect. The Settings page is now reached via the
    // top-right avatar menu → Settings (unified UserSettingsPage), auto-mounted
    // by createShell() via `config.settings` at the canonical path `/settings`.
    // This hidden route keeps the old sidebar path bookmark working.
    // Plan: web_shell_unified_settings_page_rollout_plan_2026-05-22.
    title: "_legacy",
    routes: [
      {
        path: "settings",
        label: "Settings (redirect)",
        icon: Settings,
        loader: () => import("@plerity/dashboard-shell/SettingsRedirect"),
        hidden: true,
      },
    ],
  },
];

declare const __CF_PAGES__: boolean;
const basename = typeof __CF_PAGES__ !== "undefined" && __CF_PAGES__ ? "/" : "/apps/booking";

// Booking-specific settings tabs injected into the unified UserSettingsPage.
// Reached via top-right avatar → Settings (auto-mounted at /settings by createShell).
// Plan: web_shell_unified_settings_page_rollout_plan_2026-05-22.
const settingsTabs: SettingsTab[] = [
  {
    id: "booking-settings",
    label: "Booking Settings",
    icon: Settings,
    content: <BookingSettingsTabContent />,
  },
];

const Shell = createShell({
  name: "booking",
  displayName: "Booking & Scheduling",
  shortTitle: "Booking",
  sidebarGroups,
  defaultRoute: "/dashboard",
  currentApp: "booking",
  basename,
  settingsRoute: "/settings",
  // @allow-hardcoded — Hub SSO security settings URL; cannot be env-var in CF Pages static SPA build
  securityRoute: PORTAL_URLS.SECURITY,
  auth: {
    required: !import.meta.env.DEV,
    entitlement: "booking",
  },
  chat: {
    enabled: true,
    features: ["calendar", "bookings", "services", "availability", "staff", "customers", "reminders", "settings"],
  },
  testerNotes: {
    enabled: true,
    contextId: "web-booking",
  },
  // Unified Settings page — reached via top-right avatar → Settings.
  // Profile / Appearance / Notifications / Tester Notes (admin-only) /
  // Assistant / Privacy are auto-included by UserSettingsPage; we inject
  // the booking-specific tab here.
  // Plan: web_shell_unified_settings_page_rollout_plan_2026-05-22.
  settings: {
    additionalTabs: settingsTabs,
    assistantName: "Booking Assistant",
  },
});

function App() {
  // No app-root ConfirmProvider: it statically pulls ConfirmDialog → Modal → react-aria (~30 KB gz) into the initial
  // bundle. The only useConfirm() consumer (PoliciesSection) gets its provider inside the lazy SettingsPage chunk.
  return <Shell />;
}

export default App;
